import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Edit2, Check, X, Lock, Shield, MessageCircle, FileText, Camera, Upload, Loader2 } from 'lucide-react';
import { Header } from '../components/Header';

export default function Profile({ navigate }) {
  const { user, profile, isAdmin, fetchProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(profile?.username || '');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleUpdateUsername = async () => {
    if (!username.trim()) return;
    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({ username: username.trim() })
        .eq('id', user.id);

      if (updateError) throw updateError;
      await fetchProfile(user.id);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB');
      return;
    }

    setUploadingPhoto(true);
    setError('');

    try {
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        // Always save to localStorage first (most reliable)
        localStorage.setItem(`sage_avatar_${user.id}`, base64);
        
        // Try to update in Supabase as well
        try {
          const { error: updateError } = await supabase
            .from('users')
            .update({ avatar_url: base64 })
            .eq('id', user.id);

          if (updateError) {
            console.log('Supabase avatar update failed, using localStorage:', updateError.message);
          }
        } catch (dbError) {
          console.log('Supabase update failed, using localStorage fallback');
        }
        
        // Refresh to show new avatar
        await fetchProfile(user.id);
        setUploadingPhoto(false);
        
        // Force re-render by updating a state
        window.dispatchEvent(new Event('storage'));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  // Get avatar - check localStorage first (most reliable), then profile
  const avatarUrl = localStorage.getItem(`sage_avatar_${user?.id}`) || profile?.avatar_url;

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Unknown';

  return (
    <div className="min-h-screen bg-[#08080c] pb-20" data-testid="profile-screen">
      <Header navigate={navigate} title="Profile" />

      {/* Profile Card */}
      <div className="p-4">
        <div className="bg-[#0d0d12] border border-[#1a1a24] p-6" data-testid="profile-card">
          {/* Avatar */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative group">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt="Profile" 
                  className="w-20 h-20 rounded-full object-cover border-2 border-[#00d4ff]/30"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div className="w-20 h-20 rounded-full border-2 border-[#00d4ff]/30 flex items-center justify-center bg-[#00d4ff]/5">
                  <span className="font-mono text-2xl text-[#00d4ff]">
                    {profile?.username?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              
              {/* Upload overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                data-testid="upload-avatar-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 text-[10px] text-[#00d4ff] font-mono hover:underline flex items-center gap-1"
              disabled={uploadingPhoto}
            >
              <Upload className="w-3 h-3" />
              {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
            </button>

            {/* Username */}
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-sm font-mono focus:border-[#00d4ff]/50 outline-none"
                  data-testid="username-edit-input"
                />
                <button
                  onClick={handleUpdateUsername}
                  disabled={loading}
                  className="p-2 border border-[#00ff88]/30 text-[#00ff88] hover:bg-[#00ff88]/10"
                  data-testid="save-username-btn"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditing(false); setUsername(profile?.username || ''); }}
                  className="p-2 border border-[#ff3366]/30 text-[#ff3366] hover:bg-[#ff3366]/10"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="font-mono text-base font-medium text-[#e8e8f0]">{profile?.username || 'User'}</h2>
                <button
                  onClick={() => setEditing(true)}
                  className="p-1.5 hover:bg-[#111118] transition-colors"
                  data-testid="edit-username-btn"
                >
                  <Edit2 className="w-3.5 h-3.5 text-[#4a4a60]" />
                </button>
              </div>
            )}

            {error && <p className="text-[#ff3366] text-xs mt-2 font-mono">{error}</p>}

            <p className="text-[#7a7a9a] text-xs mt-1">{user?.email}</p>
            <p className="text-[#4a4a60] text-[10px] mt-1 font-mono">Member since {memberSince}</p>

            {isAdmin && (
              <div className="mt-3 border border-[#00d4ff]/30 bg-[#00d4ff]/5 px-3 py-1">
                <span className="text-[#00d4ff] font-mono text-[10px] uppercase tracking-wider">Admin</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3" data-testid="profile-stats">
            <div className="bg-[#08080c] border border-[#1a1a24] p-3 text-center">
              <MessageCircle className="w-4 h-4 text-[#4a4a60] mx-auto mb-2" />
              <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Messages</p>
              <p className="text-lg text-[#e8e8f0] mt-1">--</p>
            </div>
            <div className="bg-[#08080c] border border-[#1a1a24] p-3 text-center">
              <FileText className="w-4 h-4 text-[#4a4a60] mx-auto mb-2" />
              <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Posts</p>
              <p className="text-lg text-[#e8e8f0] mt-1">--</p>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-4 space-y-2">
          <button
            onClick={() => navigate('settings')}
            className="w-full bg-[#0d0d12] border border-[#1a1a24] p-4 flex items-center gap-3 hover:border-[#00d4ff]/30 transition-all group"
            data-testid="goto-settings"
          >
            <Lock className="w-4 h-4 text-[#4a4a60] group-hover:text-[#00d4ff] transition-colors" />
            <span className="text-xs text-[#7a7a9a] group-hover:text-[#e8e8f0] transition-colors">Security & Settings</span>
          </button>
          
          {isAdmin && (
            <button
              onClick={() => navigate('admin')}
              className="w-full bg-[#0d0d12] border border-[#00d4ff]/20 p-4 flex items-center gap-3 hover:border-[#00d4ff]/50 hover:shadow-[0_0_15px_rgba(0,212,255,0.1)] transition-all"
              data-testid="goto-admin"
            >
              <Shield className="w-4 h-4 text-[#00d4ff]" />
              <span className="text-xs text-[#00d4ff]">Admin Panel</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
